'use client'

import { Button, Spacer, Validation } from '@vinted/web-ui'
import { useState } from 'react'

import { useSelectTypeUrl } from '@marketplace-web/domain/identity'
import { createDigitalProductPassportDraftItem } from '@marketplace-web/pro-integrations/pro-integrations-data'
import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { navigateToPage } from '@marketplace-web/shared/browser'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session'
import { urlWithParams } from '@marketplace-web/shared/utils'
import { clickEvent } from '_libs/common/event-tracker/events'
import { DIGITAL_PRODUCT_PASSPORT_URL, EDIT_ITEM_URL, SIGNUP_URL } from 'constants/routes'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'

const DIGITAL_PRODUCT_PASSPORT_DEEPLINK = (dppRef: string) =>
  `vinted://digital_product_passport/list?ref=${dppRef}`

const SellButton = ({ dppRef }: { dppRef: string }) => {
  const translate = useTranslate('business.digital_product_passport.sell_button')
  const errorTranslate = useTranslate('error_state')
  const { isWebview, user } = useSession()
  const { track } = useTracking()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isError, setIsError] = useState(false)
  const isNonNativeFlowAbTestEnabled = useAbTest('non_native_flow')?.variant === 'on'
  const selectTypeUrl = useSelectTypeUrl()

  const handleClick = () => {
    track(
      clickEvent({
        screen: Screen.DigitalProductPassport,
        target: ClickableElement.Sell,
        targetDetails: dppRef,
      }),
    )

    if (isWebview) {
      window.location.href = DIGITAL_PRODUCT_PASSPORT_DEEPLINK(dppRef)

      return
    }

    if (!user?.id) {
      navigateToPage(
        urlWithParams(isNonNativeFlowAbTestEnabled ? selectTypeUrl : SIGNUP_URL, {
          ref_url: DIGITAL_PRODUCT_PASSPORT_URL(dppRef),
        }),
      )

      return
    }

    setIsSubmitting(true)
    setIsError(false)
    createDigitalProductPassportDraftItem(dppRef)
      .then(resp => {
        if ('errors' in resp) {
          setIsError(true)

          return
        }

        const draftItemId = resp.draft_item_id
        navigateToPage(EDIT_ITEM_URL(draftItemId))
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  return (
    <div className="digital-product-passport_sell-button">
      {isError && (
        <>
          <Validation text={errorTranslate('title')} theme="warning" />
          <Spacer size={Spacer.Size.Large} />
        </>
      )}
      <Button
        type={Button.Type.Button}
        styling={Button.Styling.Filled}
        onClick={handleClick}
        disabled={isSubmitting}
        testId="digital-product-passport-sell-button"
      >
        {translate('title')}
      </Button>
    </div>
  )
}

export default SellButton
